import React, { useEffect } from 'react'
import { ReactComponent as IconClose } from '../../../images/icons/icon_close.svg'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { Button, SelectInput } from '@demandlocal/components'
import { useGetVendorAdAccounts } from '../../../api/dealership/dsp/adAccounts/useGetVendorAdAccounts'
import { VENDOR } from '../../../lib/utils/vendors'
import { useGetAgencyVendorCampaignsByVendorId } from '../../../api/agencies/dsp/useGetAgencyVendorCampaignsByVendorId'
import { useSaveSelfServiceCampaign } from '../../../api/dealership/dsp/campaigns/useSaveSelfServiceCampaign'
import { toast } from 'react-hot-toast'
import * as portals from 'react-reverse-portal'
import { useDealership } from '../../../store/zustand/dealershipStore'
import { useRefetchVendorAdAccounts } from '../../../api/dealership/dsp/adAccounts/useRefetchVendorAdAccounts'
import { queryClient } from '../../../api/queryClient'

type FieldValues = {
  account: any
  campaigns: any
}
export default function GoogleAdsCampaignsImportForm() {
  const navigate = useNavigate()
  const { agency_id } = useParams()
  const dealership = useDealership()
  const formContext = useForm<FieldValues>({})
  const { data, isLoading } = useGetVendorAdAccounts({
    vendor_id: VENDOR.Google,
    force_refresh: false,
  })

  const {
    mutateAsync: refetchVendorAdAccounts,
    isLoading: isRefetchingVendorAdAccounts,
  } = useRefetchVendorAdAccounts()
  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = formContext

  const account = watch('account')
  const { mutateAsync } = useSaveSelfServiceCampaign<'Google'>()
  const { data: campaigns, isLoading: isLoadingVendorCampaigns } =
    useGetAgencyVendorCampaignsByVendorId({
      agency_id,
      vendor_id: VENDOR.Google,
      params: account
        ? {
            customer_account_id: account?.value,
            login_customer_id: account?.login_customer_id,
          }
        : {},
    })

  const groupedCampaignsByStatus = (campaigns ?? [])
    .reduce((acc, campaign) => {
      const status = acc?.find((item) => item.value === campaign.status)
      if (!status) {
        return [
          ...acc,
          {
            label: `${campaign.status_name} Campaigns`,
            value: campaign.status,
            options: [campaign],
          },
        ]
      } else {
        return acc.map((item) => {
          if (item.value === campaign.status) {
            return {
              ...item,
              options: [...item.options, campaign],
            }
          }
          return item
        })
      }
    }, [])
    .toSorted((a, b) => a.value - b.value)

  useEffect(() => {
    setValue('campaigns', null)
  }, [account?.value, setValue])

  const onSubmit = async (values: FieldValues) => {
    const toastId = toast.loading('Importing...')
    await mutateAsync({
      data: {
        vendor_id: VENDOR.Google,
        agency_id,
        customer_account_id: account?.value,
        login_customer_id: account?.login_customer_id,
        account_name: account?.label,
        campaign_ids: values.campaigns.map((campaign: any) => campaign.value),
      },
    })
    await queryClient.invalidateQueries([
      'dealership',
      dealership?.id,
      'campaigns',
      'self-service',
      'list',
    ])
    toast.success('Imported successfully.', { id: toastId })
    navigate('./../success', {
      state: {
        campaign_count: values.campaigns.length,
      },
    })
  }

  const onAccountsRefresh = async (e) => {
    e.preventDefault()
    if (isRefetchingVendorAdAccounts) return
    const toastId = toast.loading('Refreshing list... This may take a while.')
    await refetchVendorAdAccounts({ vendor_id: VENDOR.Google }).catch((err) => {
      toast.error('Something went wrong. Please try again later.', {
        id: toastId,
      })
    })
    toast.success('List refreshed successfully!', { id: toastId })
  }

  const portalNode = React.useMemo(() => portals.createHtmlPortalNode(), [])
  return (
    <FormProvider {...formContext}>
      <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-1 flex-col h-screen'}>
        <div className={'py-5 flex justify-between items-center px-12'}>
          <h1 className={' text-3xl font-bold'}>Google Ads import</h1>
          <IconClose
            className={'cursor-pointer'}
            onClick={(e) => {
              navigate('../../../../')
            }}
          />
        </div>
        <div className="flex flex-row items-center px-12 gap-5">
          <span className={'text-primary-xiketic text-base pb-2 flex-wrap'}>
            Import campaigns from your Google Ads account
          </span>
        </div>
        <div className={'h-full overflow-auto pt-10 flex flex-col gap-10 px-12'}>
          <div className={'xl:w-1/2 w-full'}>
            <SelectInput
              name={'account'}
              label={'Google Account'}
              options={data}
              isLoading={isLoading}
              multiOptionValuesPlacement={'bottom'}
              infoMessage={
                <div className={'flex gap-1'}>
                  <h3>Can't see your account?</h3>
                  <div
                    onClick={onAccountsRefresh}
                    className={
                      isRefetchingVendorAdAccounts
                        ? 'text-gainsboro-gray cursor-not-allowed'
                        : 'text-primary-persian-blue cursor-pointer hover:underline'
                    }>
                    Refresh
                  </div>
                </div>
              }
            />
          </div>
          <div>
            <div className={'xl:w-1/2 w-full gap-2.5 flex'}>
              <SelectInput
                infoMessage={
                  'Data is fetched from Google directly. It may take a few minutes to load.'
                }
                disabled={!account?.value}
                name={'campaigns'}
                label={'Campaigns'}
                options={groupedCampaignsByStatus ?? []}
                isMulti={true}
                isLoading={isLoadingVendorCampaigns}
                multiOptionsValuesRenderPortalNode={portalNode}
              />
            </div>
            <portals.OutPortal node={portalNode} />
          </div>
        </div>
        <div className={'flex justify-between py-5 border-t px-12 mt-'}>
          <Button
            variant={'tertiary'}
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault()
              navigate('..')
            }}>
            Back
          </Button>
          <Button variant={'primary'} disabled={isSubmitting}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
